import { defineStore, acceptHMRUpdate } from 'pinia';
import * as api from '@/vuex/modules/roadmaps/api/roadmaps.api.js';
import { asyncQueue } from '@/vuex/modules/roadmaps/async-queue/async-queue';
import { freezeRoadmap } from '@/vuex/modules/roadmaps/utils/roadmaps.utils.js';
import { $canSub } from '@pendo/permissions';
import mapValues from 'lodash/mapValues';

interface RoadmapUser {
    id: string;
    [key: string]: unknown;
}

interface PublishedInfo {
    id?: string;
    publishedURL?: string;
    publishedAt?: number;
    [key: string]: unknown;
}

interface Roadmap {
    id: string;
    title?: string;
    description?: string;
    type?: string;
    permission?: 'edit' | 'owner' | 'ops' | 'restricted' | string;
    lastUpdatedAt?: number;
    createdAt?: number;
    createdByUser?: RoadmapUser;
    lastUpdatedByUser?: RoadmapUser;
    publishedInfo?: PublishedInfo;
    quarterOneStartingMonth?: number;
    startDate?: number;
    endDate?: number;
    timePeriodLabels?: unknown;
    swimlanes?: unknown;
    columns?: unknown;
    items?: unknown;
    [key: string]: unknown;
}

interface RoadmapsState {
    roadmaps: Record<string, Roadmap>;
    currentRoadmapId: string | null;
}

export function getInitialState (): RoadmapsState {
    return {
        roadmaps: {},
        currentRoadmapId: null,
    };
}

export const useRoadmapsStore = defineStore('roadmaps', {
    state: getInitialState,
    getters: {
        list: (state): Record<string, Roadmap> => {
            return {
                ...state.roadmaps,
                ...(asyncQueue.queuedUpdates.roadmaps as Record<string, Roadmap>),
            };
        },
        currentRoadmap: (state): Roadmap | undefined =>
            (state.currentRoadmapId != null ? state.roadmaps[state.currentRoadmapId] : undefined),
        currentRoadmapVersionTimestamp (state): number | undefined {
            return state.currentRoadmapId != null ? state.roadmaps[state.currentRoadmapId]?.lastUpdatedAt : undefined;
        },
        editableRoadmaps (): Roadmap[] {
            return Object.values(this.list).filter((roadmap) => this.canEditRoadmap(roadmap.id));
        },
        roadmapById () {
            return (roadmapId: string): Roadmap | undefined => this.list[roadmapId];
        },
        isRoadmapOwnerOrOps (state) {
            return (roadmapId: string): boolean => {
                // eslint-disable-next-line @typescript-eslint/no-require-imports -- circular dep; runtime require breaks init cycle
                const vuexStore = require('@/vuex/store').default;
                const roadmapOwner = state.roadmaps[roadmapId]?.createdByUser?.id;
                const currentUser = vuexStore?.state?.auth?.user?.id;
                const isOpsUser = vuexStore?.getters['auth/isOps'];

                return roadmapOwner === currentUser || isOpsUser;
            };
        },
        isCurrentRoadmapViewOnly (): boolean {
            const { currentRoadmap } = this;
            if (!currentRoadmap) return true; // no roadmap loaded → treat as read-only (safe default)

            return !this.canEditRoadmap(currentRoadmap.id);
        },
        isCurrentRoadmapRestricted (): boolean {
            const { currentRoadmap } = this;
            if (!currentRoadmap) return false; // no roadmap loaded → no permission restriction (safe default; differs from viewOnly intentionally)

            const isRoadmapOwnerOrOps = this.isRoadmapOwnerOrOps(currentRoadmap.id);
            const isRestricted = currentRoadmap.permission === 'restricted';

            return isRestricted && !isRoadmapOwnerOrOps;
        },
        isTimelineRoadmap (): boolean {
            const { currentRoadmap } = this;

            return currentRoadmap?.type === 'TIMELINE';
        },
        canEditRoadmap () {
            return (roadmapId: string): boolean => {
                const roadmap = this.roadmapById(roadmapId);
                if (!roadmap) return false;
                const { permission } = roadmap;

                return permission === 'edit' || permission === 'owner' || permission === 'ops';
            };
        },
        isRoadmapCreator (): boolean {
            return !!$canSub().entity('roadmap').action('create');
        },
        isRoadmapPublisher (): boolean {
            return !!$canSub().entity('roadmap').action('publish');
        },
    },
    actions: {
        deleteByKey (roadmapId: string) {
            delete this.roadmaps[roadmapId];
        },
        setCurrentRoadmapId (roadmapId: string | null) {
            this.currentRoadmapId = roadmapId;
        },
        setMap ({ roadmaps }: { roadmaps: Record<string, Roadmap> }) {
            this.roadmaps = mapValues(roadmaps, freezeRoadmap) as Record<string, Roadmap>;
        },
        setMapAtKey ({ roadmap }: { roadmap: Roadmap }) {
            const frozenRoadmap = freezeRoadmap(roadmap);
            this.roadmaps[roadmap.id] = frozenRoadmap;
        },
        updateMap ({ roadmaps }: { roadmaps: Record<string, Roadmap> }) {
            const roadmapsUpdated = Object.assign({}, this.roadmaps, roadmaps);
            this.roadmaps = mapValues(roadmapsUpdated, freezeRoadmap) as Record<string, Roadmap>;
        },
        updateMapAtId ({ id, roadmap }: { id: string; roadmap: Partial<Roadmap> }) {
            const frozenRoadmap = freezeRoadmap(Object.assign({}, this.roadmaps[id], roadmap));
            this.roadmaps[id] = frozenRoadmap;
        },
        updateMapAtKey ({ id, key, value }: { id: string; key: string; value: unknown }) {
            const frozenRoadmap = freezeRoadmap(Object.assign({}, this.roadmaps[id], { [key]: value }));
            this.roadmaps[id] = frozenRoadmap;
        },
        async loadAll () {
            const roadmaps = await api.fetchRoadmaps();
            this.setMap({ roadmaps });
        },
        async loadOne ({ id }: { id: string }): Promise<Roadmap> {
            const roadmap = await api.fetchRoadmap(id);
            await this.setOne(roadmap);

            return roadmap;
        },
        async setOne (roadmap: Roadmap) {
            const [
                { useRoadmapsSwimlaneStore },
                { useRoadmapsColumnsStore },
                { useRoadmapsItemsStore },
            ] = await Promise.all([
                import('@/stores/roadmaps-swimlanes.store'),
                import('@/stores/roadmaps-columns.store'),
                import('@/stores/roadmaps-items.store'),
            ]);

            useRoadmapsSwimlaneStore().setMap({ swimlanes: roadmap.swimlanes as Record<string, unknown> });
            useRoadmapsColumnsStore().setMap({ columns: roadmap.columns as Record<string, unknown> });
            useRoadmapsItemsStore().setMap({ items: roadmap.items as Record<string, unknown> });
            this.setCurrentRoadmapId(roadmap.id);
            this.setMapAtKey({ roadmap });
        },
        async createRoadmap (roadmapToCreate: Partial<Roadmap>): Promise<string> {
            const createdRoadmap = await api.createRoadmap(roadmapToCreate);
            this.updateMap({ roadmaps: { [createdRoadmap.id]: createdRoadmap } });

            return createdRoadmap.id;
        },
        async updateRoadmap (roadmapToUpdate: Roadmap): Promise<Roadmap> {
            const task = async () => {
                const roadmapVersionTimestamp = this.roadmaps[roadmapToUpdate.id]?.lastUpdatedAt;
                const payload = {
                    title: roadmapToUpdate.title,
                    description: roadmapToUpdate.description,
                    quarterOneStartingMonth: roadmapToUpdate.quarterOneStartingMonth,
                    roadmapVersionTimestamp,
                };
                const updatedRoadmap = await api.updateRoadmap(roadmapToUpdate.id, payload);

                this.updateMapAtId({ id: updatedRoadmap.id, roadmap: updatedRoadmap });

                const { useRoadmapsItemsStore } = await import('@/stores/roadmaps-items.store');
                useRoadmapsItemsStore().updateMap({ items: updatedRoadmap.items });

                return updatedRoadmap;
            };

            return asyncQueue.enqueue({
                task,
                updates: { [roadmapToUpdate.id]: roadmapToUpdate },
                updateType: 'roadmaps',
            });
        },
        async updateRoadmapSubscriptionPermissions (permission: string) {
            const { currentRoadmap } = this;
            if (!currentRoadmap) return;

            const updatedRoadmap = await api.updateRoadmapSubscriptionPermissions(currentRoadmap.id, { permission });
            this.setMapAtKey({ roadmap: updatedRoadmap });
        },
        async addRoadmapUserPermissions (userPermissionsToAdd: unknown) {
            const { currentRoadmap } = this;
            if (!currentRoadmap) return;

            const updatedRoadmap = await api.addRoadmapUserPermissions(currentRoadmap.id, userPermissionsToAdd);
            this.setMapAtKey({ roadmap: updatedRoadmap });
        },
        async updateRoadmapUserPermission (userPermission: { userId: string; permission: string }) {
            const { currentRoadmap } = this;
            if (!currentRoadmap) return;

            const payload = { permission: userPermission.permission };
            const updatedRoadmap = await api.updateRoadmapUserPermission(currentRoadmap.id, userPermission.userId, payload);
            this.setMapAtKey({ roadmap: updatedRoadmap });
        },
        async removeRoadmapUserPermission (userId: string) {
            const { currentRoadmap } = this;
            if (!currentRoadmap) return;

            const updatedRoadmap = await api.removeRoadmapUserPermission(currentRoadmap.id, userId);
            this.setMapAtKey({ roadmap: updatedRoadmap });
        },
        setCurrentRoadmapCreatedUpdatedInfo ({
            createdAt,
            createdByUser,
            lastUpdatedAt,
            lastUpdatedByUser,
        }: Partial<Roadmap>) {
            const { currentRoadmap } = this;
            if (!currentRoadmap) return;

            const updatedRoadmap = Object.assign({}, currentRoadmap, {
                createdAt,
                createdByUser,
                lastUpdatedAt,
                lastUpdatedByUser,
            });
            this.updateMapAtId({ id: currentRoadmap.id, roadmap: updatedRoadmap });
        },
        setCurrentRoadmapPublishInfo (publishedInfo: PublishedInfo) {
            const { currentRoadmap } = this;
            if (!currentRoadmap) return;

            const updatedRoadmap = Object.assign({}, currentRoadmap, { publishedInfo });
            this.setMapAtKey({ roadmap: updatedRoadmap });
        },
        async publishRoadmap (roadmapId: string): Promise<Roadmap> {
            const roadmap = this.roadmapById(roadmapId);
            if (!roadmap) throw new Error(`publishRoadmap: roadmap ${roadmapId} not in store`);

            const task = async () => {
                const updatedRoadmap = await api.publishRoadmap(roadmapId);
                await this.setOne(updatedRoadmap);

                return updatedRoadmap;
            };

            return asyncQueue.enqueue({
                task,
                updates: {
                    [roadmap.id]: { ...roadmap, publishedInfo: { publishedAt: roadmap.lastUpdatedAt } },
                },
                updateType: 'roadmaps',
            });
        },
        async unpublishRoadmap (roadmapId: string): Promise<Roadmap> {
            const roadmap = this.roadmapById(roadmapId);
            if (!roadmap) throw new Error(`unpublishRoadmap: roadmap ${roadmapId} not in store`);

            const task = async () => {
                const updatedRoadmap = await api.unpublishRoadmap(roadmapId);
                await this.setOne(updatedRoadmap);

                return updatedRoadmap;
            };

            return asyncQueue.enqueue({
                task,
                updates: {
                    [roadmap.id]: {
                        ...roadmap,
                        publishedInfo: { id: '', publishedURL: '', publishedAt: 0 },
                    },
                },
                updateType: 'roadmaps',
            });
        },
        async deleteRoadmap (roadmapId: string) {
            await api.deleteRoadmap(roadmapId);
            this.deleteByKey(roadmapId);
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useRoadmapsStore, module.hot));
}
